/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import styled from "@emotion/styled";
import { spacing } from "@atlas-int/tokens";
import { Text } from "@atlas-int/ui";

const SectionContainer = styled.div`
  margin-bottom: ${spacing[8]};
`;

import { Link } from "react-router";

// Dataset name mapping based on repository value (same as DatasetModal)
const datasetNameMapping: Record<string, string> = {
  hs92: "International Trade Data (HS92)",
  hs12: "International Trade Data (HS12)",
  hs22: "International Trade Data (HS22)",
  sitc: "International Trade Data (SITC, Rev. 2)",
  services_unilateral: "International Trade Data (Services)",
  classification: "Classifications Data",
  rankings: "Growth Projections and Complexity Rankings",
  product_space: "Product Space Networks",
};

interface DownloadItem {
  repo?: string | null;
  doi?: string | null;
  dvPublicationDate?: string | null;
  [key: string]: unknown;
}

interface DataSourcesSectionProps {
  downloadsData?: DownloadItem[];
}

const DataSourcesSection = ({ downloadsData }: DataSourcesSectionProps) => {
  // Get the first dataset with a DOI from the API data
  const exampleCitation = useMemo(() => {
    if (!downloadsData || downloadsData.length === 0) {
      return null;
    }

    const firstWithDoi = downloadsData.find((d) => d.doi);
    if (!firstWithDoi) {
      return null;
    }

    const publicationYear = firstWithDoi.dvPublicationDate
      ? new Date(firstWithDoi.dvPublicationDate).getFullYear()
      : new Date().getFullYear();

    return {
      name: datasetNameMapping[firstWithDoi.repo] || firstWithDoi.repo,
      year: publicationYear,
      doi: firstWithDoi.doi,
      doiUrl: `https://doi.org/${firstWithDoi.doi}`,
    };
  }, [downloadsData]);
  return (
    <SectionContainer>
      <section>
        <Text variant="heading-2" style={{ marginTop: "0" }}>
          API Access
        </Text>
        <Text variant="body">
          Atlas users can query data directly through our GraphQL API.
          Documentation on access procedures and limitations is available on
          this{" "}
          <a
            href="https://github.com/harvard-growth-lab/api-docs/blob/main/atlas.md"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub page
          </a>
          .
        </Text>
      </section>

      <section>
        <Text variant="heading-2">Growth Lab Trade Data Methodology</Text>
        <Text variant="body">
          International trade flows are recorded twice: once by exporters and
          once by importers. In practice, these records often differ, are
          incomplete, or are reported using different product classifications.
          To address these issues, the Growth Lab developed a standardized,
          data-driven process to produce consistent and comparable bilateral
          trade estimates.
        </Text>
        <Text variant="body">
          Our methodology combines two core steps: reconciling partner reports
          and harmonizing product classifications over time.
        </Text>

        <Text variant="heading-3">
          Reconciling Exporter and Importer Reports
        </Text>
        <Text variant="body">
          We first adjust reported values to ensure comparability between
          exporter-reported (FOB) and importer-reported (CIF) data. We then
          assess the reliability of each country's reporting based on its
          consistency across trading partners. Using these reliability scores,
          we combine exporter and importer reports into a single estimated trade
          value that places greater weight on more reliable sources.
        </Text>

        <Text variant="heading-3">Harmonizing Product Classifications</Text>
        <Text variant="body">
          Countries adopt new trade classification systems at different times,
          and product codes are regularly revised, split, or merged. To ensure
          comparability across years, we use data-driven conversion weights that
          translate trade values between classification vintages while
          preserving detailed product information. This approach avoids the loss
          of products and discontinuities that arise from simple one-to-one
          mappings.
        </Text>
        <Text variant="body">
          By integrating reliability-weighted mirroring with weighted product
          conversion, we generate bilateral, product-level trade datasets that
          are consistent across countries and over time. These datasets form the
          core data underlying the Atlas and improve coverage, reduce reporting
          discrepancies, and support long-run analysis of global trade patterns.
        </Text>
        <Text variant="body">
          A full description of this methodology is available in our
          peer-reviewed paper:
        </Text>
        <Text variant="body">
          <em>
            Bustos et al. (2026), Tackling Discrepancies in Trade Data: The
            Harvard Growth Lab International Trade Datasets, Scientific Data.
          </em>
        </Text>
        <Text variant="body">
          A visual description of the Growth Lab's trade data methodology is
          available on our{" "}
          <a
            href="https://atlas.hks.harvard.edu/trade-data-methodology"
            target="_blank"
            rel="noopener noreferrer"
          >
            companion website
          </a>
          .
        </Text>
      </section>

      <section>
        <Text variant="heading-2">Data Sources</Text>
        <Text variant="body">
          The Atlas uses data from the following international organizations as
          inputs into its comprehensive methodology:
        </Text>
        <ul>
          <li>
            <Text variant="body">
              <strong>Goods Trade:</strong> United Nations Statistical Division
              (UN Comtrade)
            </Text>
          </li>
          <li>
            <Text variant="body">
              <strong>Services Trade:</strong> International Monetary Fund,
              Direction of Trade Statistics (DOTS)
            </Text>
          </li>
          <li>
            <Text variant="body">
              <strong>Economic Indicators:</strong> International Monetary Fund,
              World Economic Outlook (WEO)
            </Text>
          </li>
          <li>
            <Text variant="body">
              <strong>Price Adjustments:</strong> Federal Reserve Economic Data
              (FRED), Producer Price Index for Industrial Commodities
            </Text>
          </li>
        </ul>
        <Text variant="body">
          Atlas trade values are reported in U.S. dollars (USD). Constant-dollar
          values are adjusted using the FRED Producer Price Index for Industrial
          Commodities, with the base year set to the most recent Atlas data
          year. This allows users to interpret values in terms of current
          purchasing power.
        </Text>
        <Text variant="body">
          For historical analysis (e.g., growth rates and long-run trends),
          constant-dollar values are recommended.
        </Text>
      </section>

      <section>
        <Text variant="heading-2">Data Classifications</Text>

        <Text variant="heading-3">Product Classifications</Text>
        <Text variant="body">
          The Atlas provides trade data using two complementary classification
          systems:
        </Text>

        <Text variant="body">
          <strong>Harmonized System (HS)</strong>
        </Text>
        <Text variant="body">
          Covers approximately 5,000–5,600 products using 6-digit codes (exact
          counts vary by revision). Best suited for analyzing contemporary
          products and industries. HS classifications are available in the Atlas
          in three revisions:
        </Text>
        <ul>
          <li>
            <Text variant="body">HS 1992: data available from 1995 onward</Text>
          </li>
          <li>
            <Text variant="body">HS 2012: data available from 2012 onward</Text>
          </li>
          <li>
            <Text variant="body">HS 2022: data available from 2022 onward</Text>
          </li>
        </ul>

        <Text variant="body">
          <strong>Standard International Trade Classification (SITC)</strong>
        </Text>
        <Text variant="body">
          Provides a harmonized, long-run dataset spanning multiple
          classification systems from 1962 to the present. Covers approximately
          700 products using up to 4-digit codes. Best suited for long-term
          historical analysis and trend comparison. SITC data in the Atlas is
          based upon SITC Rev. 2.
        </Text>

        <Text variant="heading-3">Services Trade</Text>
        <Text variant="body">
          Services exports and imports are reported unilaterally, with partner
          countries grouped as "Services Partners." Coverage begins in 1980 and
          varies by country, with data available for approximately 50 to 75
          percent of Atlas countries.
        </Text>
      </section>

      <section>
        <Text variant="heading-2">Country Coverage</Text>
        <Text variant="body">
          While Atlas Explore includes data for all countries and territories
          covered by UN Comtrade, Atlas Country Profiles and Rankings are
          limited to countries that meet minimum coverage and quality standards:
        </Text>
        <ul>
          <li>
            <Text variant="body">Population of at least 1 million</Text>
          </li>
          <li>
            <Text variant="body">
              Average annual trade volume of at least $1 billion
            </Text>
          </li>
          <li>
            <Text variant="body">
              Verified GDP and export data availability
            </Text>
          </li>
          <li>
            <Text variant="body">
              Consistent and reliable trade reporting history
            </Text>
          </li>
        </ul>
        <Text variant="body">
          The Atlas aims to reflects countries as they exist today, following UN
          recognition. Map boundaries come from United Nations geospatial data,
          chosen for its stability over time and its alignment with our trade
          data sources. When country names, borders, or political status change,
          the Atlas may take time to reflect those updates as we revise the
          underlying data and visualisations.
        </Text>
      </section>

      <section>
        <Text variant="heading-2">Data Update Cycle</Text>
        <Text variant="body">
          Atlas data updates follow international trade reporting cycles and
          require substantial processing to ensure quality and consistency.
        </Text>

        <Text variant="heading-3">Annual Updates</Text>
        <Text variant="body">
          Approximately 95 percent of Atlas data is updated once per year,
          typically between April and June. This reflects country reporting
          timelines to UN Comtrade, which generally require 12 to 18 months to
          reach sufficient coverage. For example, most 2024 trade data becomes
          available in the Atlas between April and June 2026.
        </Text>
        <Text variant="body">
          Annual releases may incorporate late or corrected country reporting
          which can lead to small revisions in historical data. This process
          improves long-run accuracy and consistency.
        </Text>

        <Text variant="heading-3">Interim Updates</Text>
        <Text variant="body">
          Throughout the year, we also release interim updates that incorporate
          newly submitted country data, reflect improvements in data processing
          methods, and address minor corrections when needed.
        </Text>
        <Text variant="body">
          For real-time information on country-level data availability, see the{" "}
          <a
            href="https://comtradeplus.un.org/Visualization/DADashboard"
            target="_blank"
            rel="noopener noreferrer"
          >
            UN Comtrade Data Availability Dashboard
          </a>
          .
        </Text>
      </section>

      <section>
        <Text variant="heading-2">Usage and Citation</Text>
        <Text variant="body">
          The Atlas of Economic Complexity is a freely available public
          resource. Its data and tools are widely used in research, policy
          analysis, and applied projects. We encourage reuse and adaptation,
          provided that users cite the Atlas and underlying data sources
          appropriately.
        </Text>

        <Text variant="heading-3">Citation Guidelines</Text>
        <Text variant="body">
          When incorporating Atlas content in your work, please use these
          citation formats:
        </Text>

        <Text variant="body">
          <strong>For the Atlas Platform:</strong>
        </Text>
        <Text variant="body">
          Growth Lab at Harvard University. "The Atlas of Economic Complexity."
          Web application. Harvard Kennedy School, 2024.
          https://atlas.hks.harvard.edu
        </Text>

        <Text variant="body">
          <strong>For Downloaded Datasets:</strong>
        </Text>
        <Text variant="body">
          Each downloadable data file includes specific citation requirements.
        </Text>
        {exampleCitation && (
          <Text variant="body">
            Example: The Growth Lab at Harvard University. (
            {exampleCitation.year}
            ). "{exampleCitation.name}" [Data set].{" "}
            <a
              href={exampleCitation.doiUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {exampleCitation.doiUrl}
            </a>
          </Text>
        )}

        <Text variant="body">
          <strong>For the Original Atlas Book:</strong>
        </Text>
        <Text variant="body">
          Hausmann, R., Hidalgo, C., Bustos, S., Coscia, M., Chung, S., Jimenez,
          J., Simoes, A., Yildirim, M. (2013). The Atlas of Economic Complexity.
          Cambridge, MA: MIT Press.
        </Text>
      </section>

      <section>
        <Text variant="heading-2">Support</Text>
        <Text variant="body">
          We provide several resources to help users understand and work with
          Atlas data:
        </Text>
        <ul>
          <li>
            <Text variant="body">
              <Link to="/glossary" target="_blank" rel="noopener noreferrer">
                Glossary
              </Link>
              : Definitions of key terms, indicators, and classifications
            </Text>
          </li>
          <li>
            <Text variant="body">
              <Link to="/faq" target="_blank" rel="noopener noreferrer">
                FAQ
              </Link>
              : Answers to common questions about data sources, methods, and
              interpretation
            </Text>
          </li>
          <li>
            <Text variant="body">
              <a
                href="https://github.com/harvard-growth-lab"
                target="_blank"
                rel="noopener noreferrer"
              >
                GitHub Repositories
              </a>
              : Technical documentation and data access tools
            </Text>
          </li>
        </ul>
        <Text variant="body">
          For technical questions, research use cases, or data access requests,
          contact us at{" "}
          <a href="mailto:growthlabtools@hks.harvard.edu">
            growthlabtools@hks.harvard.edu
          </a>
          .
        </Text>
      </section>
    </SectionContainer>
  );
};

export default DataSourcesSection;
